<script setup lang="ts">
  import type { ICityItem, ICountryItem } from '@/types/BestBuyBanners'
  import type { ISimpleSelectItem } from '@/components/ui/select/types'
  import { SHOW_ALL_ID } from '@/components/best-buy-banner/constants'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'

  const props = withDefaults(
    defineProps<{
      originCountries: ICountryItem[]
      originCities: Map<string, ICityItem[]>
      darkTheme?: boolean
    }>(),
    {
      bannersData: null,
      darkTheme: false
    }
  )

  const originCountry = defineModel<ISimpleSelectItem>('originCountry')
  const originCity = defineModel<ISimpleSelectItem>('originCity')

  const selectedOriginCountry = ref<ISimpleSelectItem>()

  const { t } = useI18n()
  const originCountries = computed<ISimpleSelectItem[]>(
    () =>
      props?.originCountries?.map((origin) => ({
        id: origin.country,
        label: origin.countryLabel !== 'ZZ' ? origin.countryLabel : t('country.other')
      })) ?? []
  )

  const SHOW_ALL_CITIES_OPTION = {
    id: SHOW_ALL_ID,
    label: t('clp.showAll')
  }

  function getOriginCitiesSelectItems() {
    const activeOriginCities = props.originCities?.get(selectedOriginCountry.value?.id ?? '')

    if (!activeOriginCities?.length || activeOriginCities.length === 1) {
      return []
    }

    const selectItems =
      activeOriginCities.map((city) => ({
        id: city.city,
        label: city.cityLabel
      })) ?? []

    return [SHOW_ALL_CITIES_OPTION, ...selectItems]
  }

  const isDefaultCountrySet = ref(false)
  const isDefaultCitySet = ref(false)
  const originCitiesSelectItems = ref()

  const { pushBestBuyBannerDataLayer } = useIndexPageDataLayer()

  function onOriginCountryChange(country?: ISimpleSelectItem, sendDataLayer: boolean = false) {
    if (!country) {
      return
    }

    if (sendDataLayer && selectedOriginCountry.value) {
      pushBestBuyBannerDataLayer({
        event_category: 'country_switch',
        event_action: selectedOriginCountry.value.id,
        event_label: country.id
      })
    }

    selectedOriginCountry.value = country
    originCountry.value = country

    originCity.value = SHOW_ALL_CITIES_OPTION
    originCitiesSelectItems.value = getOriginCitiesSelectItems()

    isDefaultCountrySet.value = true
    setOriginCitySelected()
  }

  function onOriginCityChange(city?: ISimpleSelectItem, sendDataLayer: boolean = false) {
    if (!city) return
    if (originCity.value?.id === city.id) return

    if (sendDataLayer && originCity.value) {
      pushBestBuyBannerDataLayer({
        event_category: 'city_switch',
        event_action: selectedOriginCountry.value?.id,
        event_label: city.id
      })
    }

    originCity.value = city
    setOriginCitySelected()
  }

  function setOriginCitySelected() {
    isDefaultCitySet.value = true
  }

  const localeStore = useLocaleStore()

  watch(
    () => originCountries.value,
    (val) => {
      const selectedCountry = val.find((country) => country.id === localeStore.selectedCountry)
      if (selectedCountry) {
        onOriginCountryChange(selectedCountry)

        return
      }

      onOriginCountryChange(val[0])
    },
    { immediate: true }
  )

  const selectClass = ['text-bt-blue-ui border-bt-blue-ui', { 'md:text-bt-green md:border-bt-green': !props.darkTheme }]
  const titleClass = ['text-bt-blue-dark', { 'md:text-white': !props.darkTheme }]
</script>
<template>
  <div class="flex flex-col items-start gap-1 md:flex-row md:gap-4">
    <div class="flex flex-row gap-1 md:gap-4">
      <p
        :id="`origin-country-label-${selectedOriginCountry?.id}`"
        class="mb-0 text-h5"
        :class="titleClass"
      >
        {{ $t('clp.flightsFrom') }}
      </p>
      <UiSelectSimple
        :model-value="selectedOriginCountry"
        :items="originCountries"
        :trigger-class="selectClass"
        :aria-labelled-by="`origin-country-label-${selectedOriginCountry?.id}`"
        @update:model-value="(country) => onOriginCountryChange(country, true)"
      />
    </div>
    <div
      v-if="originCitiesSelectItems?.length > 1"
      class="flex flex-row gap-2 md:gap-4"
    >
      <p
        :id="`origin-city-label-${originCity?.id}`"
        class="mb-0 text-h5"
        :class="titleClass"
      >
        {{ $t('clp.cityOfDeparture') }}
      </p>
      <UiSelectSimple
        v-model="originCity"
        :items="originCitiesSelectItems"
        :trigger-class="selectClass"
        :aria-labelled-by="`origin-city-label-${originCity?.id}`"
        @update:model-value="(city) => onOriginCityChange(city, true)"
      />
    </div>
  </div>
</template>
